import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import LOGO_BASE64 from "../../assets/data/logoBase64";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  IconButton,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { ArrowBack } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { MdPhoneAndroid, MdLocationOn } from "react-icons/md";
import Stack from "@mui/material/Stack";
import { FaTrash, FaEdit, FaPrint } from "react-icons/fa";
import TrayBalanceModal from "../../components/TrayBalanceModal";
import TransactionReceipt from "../../components/TransactionReceipt";
import { useAuth } from "../../context/AuthContext";
import { useRef } from "react";
import { getTrays } from "../../actions";

const TRANSACTION_FORM_DEFAULTS = {
  stockGiven: false,
  paymentDone: false,
  traysReturned: false,
  dateOfTransaction: null,
  paymentAmount: "",
  sales: [
    {
      itemName: "",
      trayTypeId: undefined,
      numberOfItems: undefined,
      costPerItem: undefined,
    },
  ],
  trays: [
    {
      trayTypeId: undefined,
      numberOfTrays: undefined,
      excludePayment: false
    },
  ],
};

/** Transaction Validation Schema */
const transactionSchema = Yup.object().shape({
  stockGiven: Yup.boolean().default(false),
  paymentDone: Yup.boolean().default(false),
  traysReturned: Yup.boolean().default(false),

  dateOfTransaction: Yup.date()
    .nullable()
    .when(["stockGiven", "paymentDone", "traysReturned"], {
      is: (stockGiven, paymentDone, traysReturned) =>
        Boolean(stockGiven) || Boolean(paymentDone) || Boolean(traysReturned),
      then: (schema) =>
        schema
          .required("Date is required")
          .max(new Date(), "Transaction cannot be in future"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  sales: Yup.array().when("stockGiven", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            itemName: Yup.string().required("Item required"),
            numberOfItems: Yup.number()
              .required("Quantity is required")
              .positive("Quantity must be positive"),
            costPerItem: Yup.number().required("Cost required").positive(),
            trayTypeId: Yup.number(),
          })
        )
        .min(1, "At least one sale required"),
  }),

  trays: Yup.array().when("traysReturned", {
    is: true,
    then: () =>
      Yup.array()
        .of(
          Yup.object({
            numberOfTrays: Yup.number()
              .required("Quantity is required")
              .positive("Quantity must be positive"),
            trayTypeId: Yup.number().required("Tray type is required"),
            excludePayment: Yup.boolean().default(false)
          })
        )
        .min(1, "At least one tray required"),
  }),

  paymentAmount: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      )
        return null;
      const num = Number(originalValue);
      return isNaN(num) ? null : num;
    })
    .when("paymentDone", {
      is: true,
      then: (schema) =>
        schema
          .required("Amount is required")
          .positive("Amount must be positive"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
});

/**Payment Validation Schema */
const paymentSchema = Yup.object({
  amount: Yup.number()
    .typeError("Amount is required")
    .required("Amount is required")
    .positive("Amount must be greater than zero"),
  paymentDate: Yup.date()
    .required("Payment date is required")
    .max(new Date(), "Payment date cannot be in the future"),
  paymentMethod: Yup.string()
    .required("Payment method is required")
    .oneOf(["NEFT", "UPI", "Cash", "Net Banking"], "Invalid payment method"),
});

function CustomerDetails() {
  const [customerDetails, setCustomerDetails] = useState({});
  const [selectedTransactionId, setSelectedTransactionId] = useState("");
  const [open, setOpen] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transactionList, setTransactionList] = useState([]);
  const [items, setItems] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const params = useParams();
  const { id: customerId } = params;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const [openTrayModal, setOpenTrayModal] = useState(false);
  const [excludePayment, setExcludePayment] = useState(false);
  const [editTransaction, setEditTransaction] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState(null);
  const [trayTypes, setTrayTypes] = useState([]);

  const { user } = useAuth();

  /**Fetch Customer + Transactions */
  useEffect(() => {
    if (!customerId) return;

    setLoadingTx(true);

    const payload = { customerId };

    if (dateRange.startDate && dateRange.endDate) {
      payload.startDate = dayjs(dateRange.startDate)
        .startOf("day")
        .toISOString();
      payload.endDate = dayjs(dateRange.endDate).endOf("day").toISOString();
    }

    Promise.all([
      axiosInstance.post("/customers/details", { customerId }),
      axiosInstance.post("/transactions/list", payload),
      axiosInstance.get("/stock"),
      getTrays(),
    ])
      .then(([custRes, txRes, stockRes, traysList]) => {
        setCustomerDetails(custRes?.data?.result || {});
        setTransactionList(txRes?.data?.result || []);
        setItems(stockRes?.data?.list || []);
        setTrayTypes(Array.isArray(traysList) ? traysList : []);
      })
      .catch((err) =>
        toast.error(err?.response?.data?.message || "Failed to load data")
      )
      .finally(() => setLoadingTx(false));
  }, [customerId, dateRange, open]);

  /** Transaction Form */
  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(transactionSchema),
    defaultValues: TRANSACTION_FORM_DEFAULTS,
  });

  const {
    fields: salesFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "sales",
  });

  const trayForm = useFieldArray({
    control,
    name: "trays",
  });

  const stockGiven = watch("stockGiven");
  const paymentDone = watch("paymentDone");
  const traysReturned = watch("traysReturned");
  const watchedTransaction = watch();

  let filteredTrayTypes = [];
  if (Array.isArray(customerDetails?.trayBalance)) {
    filteredTrayTypes = customerDetails.trayBalance.filter(
      (type) => type.count > 0
    );
  }

  /** Payment Form */
  const paymentForm = useForm({
    resolver: yupResolver(paymentSchema),
    mode: "onTouched",
    defaultValues: {
      amount: "",
      paymentDate: null,
      paymentMethod: "",
      referenceId: "",
    },
  });

  const activeTrayTypes = trayTypes.filter(el => el.status !== false)

  const closeTransactionPopup = () => {
    setEditTransaction(false);
    setSelectedTransaction(null);

    reset(TRANSACTION_FORM_DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
    });
    setExcludePayment(false)
    setOpen(false);
  };

  const paginatedTransactions = transactionList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getTrayBalanceCount = (trayTypeId) => {
    const tray = customerDetails?.trayBalance?.find((t) => t.id === trayTypeId);
    return tray ? Number(tray.count) : 0;
  };

  const selectedTrayTypeIds = watch("trays")
    ?.map((t) => t?.trayTypeId)
    .filter(Boolean);

  const printPDF = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Transactions${(dateRange?.startDate && dateRange?.endDate) ? ` - ${new Date(dateRange?.startDate).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    })} - ${new Date(dateRange?.endDate).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    })}` : ''}</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              margin: 24px;
              color: #000;
            }
  
            h2 {
              margin: 0;
              text-transform: capitalize;
            }
  
            .header {
              border-bottom: 2px solid #000;
              padding-bottom: 8px;
              margin-bottom: 12px;
            }
  
            .meta {
              font-size: 12px;
              margin-top: 4px;
            }
  
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
              margin-top: 12px;
            }
  
            th, td {
              border: 1px solid #000;
              padding: 4px;
              vertical-align: middle;
            }
  
            thead th {
              background: #eee;
              text-align: center;
              font-weight: bold;
            }
  
            .right {
              text-align: center;
            }
  
            tr {
              page-break-inside: avoid;
            }
  
            .footer {
              margin-top: 14px;
              font-size: 10px;
              border-top: 1px solid #000;
              padding-top: 6px;
              display: flex;
              justify-content: space-between;
            }

            .amountValues {
              font-size: 12px;
              display: flex;
              justify-content: space-between;
            }
          </style>
        </head>
        <body>
          <div class="header" style="display: flex; align-items: center; justify-content: space-between">
            <div style="display: flex; align-items: center; gap: 12px">
              <img
                src=${LOGO_BASE64}
                alt="DBF Logo"
                style="width: 50px; height: 50px; border-radius: 4px"
              />
              <div>
                <div
                  style="font-size: 20px; font-weight: bold; color: #1976d2;">
                  ${user?.employerName}
                </div>
                <div style="color: #666; font-size: 16px">
                  ${customerDetails?.branchName}
                </div>
              </div>
            </div>
            <div>
              <h2 style="font-size: 20px; font-weight: bold">${customerDetails?.name || ""}</h2>
              <div style="color: #666; font-size: 16px" class="meta">
                ${customerDetails?.mobile || ""} • ${customerDetails?.location || ""
      }
              </div>
            </div>
          </div>

          <div class="amountValues">
            <span>Total Transaction Value: ${totalTransactionAmount.toLocaleString("en-IN")}</span>
            <span>Total Amount Paid: ${(totalPaidAmount + trayPaymentAmount).toLocaleString("en-IN")}</span>
            <span>Total Amount Due: ${(
        totalTransactionAmount -
        (totalPaidAmount + trayPaymentAmount)
      ).toLocaleString("en-IN")}</span>
          </div>
          <table>
          <thead>
          <tr>
            <th rowspan="2">Date</th>
            <th colspan="4">Sales</th>
            <th colspan="3">Trays Returned</th>
            <th colspan="3">Payment</th>
            <th rowspan="2">Transaction By</th>
          </tr>
          <tr>
            <th>Item</th><th>Qty</th><th>Price</th><th>Total</th>
            <th>Qty</th><th>Price</th><th>Total</th>
            <th>Cash/Online</th><th>Tray</th><th>Total</th>
          </tr>
          </thead>

          <tbody>
          ${paginatedTransactions
        .map((tx) => {
          const sales = tx.sales || [];
          const trays = tx.trays || [];
          const rowCount = Math.max(sales.length, trays.length) || 1;

          const traysTotal = trays.reduce(
            (s, t) =>
              s +
              (Number(t?.numberOfTrays) || 0) *
              (Number(t?.costPerTray) || 0),
            0
          );

          const payment = Number(tx.paymentAmount) || 0;
          const grandTotal = payment + traysTotal;

          return Array.from({ length: rowCount })
            .map((_, index) => {
              const sale = sales[index];
              const tray = trays[index];

              const trayTotal =
                tray?.numberOfTrays != null && tray?.costPerTray != null
                  ? tray.numberOfTrays * tray.costPerTray
                  : "-";

              return `
          <tr>
            ${index === 0
                  ? `
            <td class="right" rowspan="${rowCount}">
              ${tx.dateOfTransaction
                    ? new Date(tx.dateOfTransaction).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      timeZone: "Asia/Kolkata",
                    })
                    : "-"
                  }
            </td>`
                  : ""
                }

            <td class="right">${sale?.itemName ?? "-"}</td>
            <td class="right">${sale?.numberOfItems ?? "-"}</td>
            <td class="right">${sale?.costPerItem ?? "-"}</td>
            <td class="right">${sale?.total ?? "-"}</td>

            <td class="right">${tray?.numberOfTrays ?? "-"}</td>
            <td class="right">${tray?.costPerTray ?? "-"}</td>
            <td class="right">${trayTotal}</td>

            ${index === 0
                  ? `
            <td rowspan="${rowCount}" class="right">${payment}</td>
            <td rowspan="${rowCount}" class="right">${traysTotal}</td>
            <td rowspan="${rowCount}" class="right">${grandTotal}</td>
            <td rowspan="${rowCount}" class="right">${tx.transactionBy?.fullName || "-"
                  }</td>
            `
                  : ""
                }
          </tr>
          `;
            })
            .join("");
        })
        .join("")}
          </tbody>
          </table>

          <div class="footer">
            <span>Generated on: ${new Date().toLocaleString()}</span>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const closePaymentPopup = () => {
    paymentForm.reset();
    setOpenPayment(false);
  };

  const clearDateFilter = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /** Add Transaction */
  const addTransaction = async (data) => {
    let dateOfTransaction = data.dateOfTransaction;

    // If no date is provided, use current date as fallback
    if (!dateOfTransaction) {
      dateOfTransaction = new Date();
    }

    // Build payload with all checkbox states and their corresponding fields
    const payload = {
      customerId: params?.id,
      dateOfTransaction: dateOfTransaction,
      paymentAmount: data?.paymentAmount,
    };

    if (stockGiven) {
      payload.sales = data?.sales;
    }

    if (traysReturned) {
      payload.trays = data.trays.map((tray) => {
        const selectedTrayType = trayTypes.find(
          (type) => type.trayTypeId === tray.trayTypeId || type.id === tray.trayTypeId
        );
        tray.costPerTray = excludePayment ? 0 : (selectedTrayType?.price || 0);
        tray.excludePayment = excludePayment
        return tray;
      });
    }

    let path = "transactions/create";

    if (editTransaction) {
      path = "transactions/update/" + selectedTransaction._id;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(path, payload);
      toast.success(
        response?.data?.message || "Transaction saved successfully"
      );

      // Show receipt only for new transactions (not edits)
      if (!editTransaction) {
        const transactionData = {
          sales: stockGiven ? data.sales : [],
          trays: traysReturned ? payload.trays : [],
          paymentAmount: data.paymentAmount || 0,
          dateOfTransaction: dateOfTransaction,
        };
        setReceiptTransaction(transactionData);
        setShowReceipt(true);
      }

      closeTransactionPopup();
      setSaving(false);
    } catch (err) {
      setSaving(false);
      console.error("Error submitting transaction:", err); // Debug log
      toast.error(err?.response?.data?.message || "Failed to save transaction");
    }
  };

  const val = (v) => v ?? "-";

  /*Update Payment */
  const updatePayment = async (data) => {
    const payload = {
      transactionId: selectedTransactionId,
      amount: data.amount,
      paymentDate: data.paymentDate,
      paymentMethod: data.paymentMethod,
      referenceId: data.referenceId,
    };

    try {
      const response = await axiosInstance.post(
        "transactions/update-payment",
        payload
      );
      toast.success(response?.data?.message || "Payment updated successfully");
      closePaymentPopup();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update payment");
    }
  };

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const totalTransactionAmount = paginatedTransactions.reduce((sum, tx) => {
    const salesTotal = (tx.sales || []).reduce((tSum, sale) => {
      return tSum + toNumber(sale?.total);
    }, 0);
    return sum + salesTotal;
  }, 0);

  const grandTotalTransactionAmount = transactionList.reduce((sum, tx) => {
    const salesTotal = (tx.sales || []).reduce((tSum, sale) => {
      return tSum + toNumber(sale?.total);
    }, 0);
    return sum + salesTotal;
  }, 0);

  const totalPaidAmount = paginatedTransactions.reduce((sum, tx) => {
    return sum + toNumber(tx?.paymentAmount);
  }, 0);

  const grandTotalPaidAmount = transactionList.reduce((sum, tx) => {
    return sum + toNumber(tx?.paymentAmount);
  }, 0);

  const trayPaymentAmount = paginatedTransactions.reduce((sum, tx) => {
    const traysTotal = (tx.trays || []).reduce((tSum, tray) => {
      if (excludePayment) return tSum;

      return tSum + toNumber(tray?.numberOfTrays) * toNumber(tray?.costPerTray);
    }, 0);
    return sum + traysTotal;
  }, 0);

  const grandTrayPaymentAmount = transactionList.reduce((sum, tx) => {
    const traysTotal = (tx.trays || []).reduce((tSum, tray) => {
      return tSum + toNumber(tray?.numberOfTrays) * toNumber(tray?.costPerTray);
    }, 0);

    return sum + traysTotal;
  }, 0);

  const generateReceiptHTML = (tx, customerDetails, accountBalanceData) => {
    const sales = tx.sales || [];
    const trays = tx.trays || [];
    const paymentReceived = Number(tx.paymentAmount) || 0;

    const salesTotal = sales.reduce((sum, s) => sum + (Number(s.numberOfItems) * Number(s.costPerItem) || 0), 0);
    const trayPaymentTotal = trays.reduce((sum, t) => sum + (Number(t.numberOfTrays) * Number(t.costPerTray) || 0), 0);
    const totalCredits = paymentReceived + trayPaymentTotal;
    const transactionBalance = salesTotal - totalCredits;
    const accountBalance = accountBalanceData?.totalAmountDue || 0;

    return `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .receipt-container { border: 1px solid #ccc; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th, td { border: 1px solid #333; padding: 8px; }
            .primary-color { color: #1976d2; }
            .right { text-align: right; }
            /* Add the rest of your receipt styles here */
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <h2>${user?.employerName}</h2>
            <p>Customer: ${customerDetails?.name}</p>
            <p>Date: ${new Date(tx.dateOfTransaction).toLocaleDateString('en-IN')}</p>
            
            <table>
              <thead><tr><th>Item</th><th class="right">Qty</th><th class="right">Amount</th></tr></thead>
              <tbody>
                ${sales.map(s => `<tr><td>${s.itemName}</td><td class="right">${s.numberOfItems}</td><td class="right">₹${s.total}</td></tr>`).join('')}
              </tbody>
            </table>

            <div class="right">
              <p><strong>Total Due: ₹${salesTotal}</strong></p>
              <p><strong>Total Paid: ₹${totalCredits}</strong></p>
              <hr/>
              <p><strong>Balance: ₹${transactionBalance}</strong></p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const openEditTransactionPopup = (tx) => {
    setEditTransaction(true);
    setSelectedTransaction(tx);

    const hasSales = Array.isArray(tx.sales) && tx.sales.length > 0;
    const hasTrays = Array.isArray(tx.trays) && tx.trays.length > 0;
    const hasPayment = !!tx.paymentAmount;

    let trayPaymentExcluded = tx.trays.some(t => t.excludePayment)

    setExcludePayment(trayPaymentExcluded)

    reset({
      stockGiven: hasSales,
      traysReturned: hasTrays,
      paymentDone: hasPayment,

      dateOfTransaction: tx.dateOfTransaction
        ? new Date(tx.dateOfTransaction)
        : null,

      paymentAmount: tx.paymentAmount || "",

      sales: hasSales
        ? tx.sales.map((s) => ({
          itemName: s.itemName,
          numberOfItems: s.numberOfItems,
          costPerItem: s.costPerItem,
          trayTypeId: s.trayTypeId ?? undefined,
        }))
        : [
          {
            itemName: "",
            numberOfItems: undefined,
            costPerItem: undefined,
            trayTypeId: undefined,
          },
        ],

      trays: hasTrays
        ? tx.trays.map((t) => ({
          numberOfTrays: t.numberOfTrays,
          trayTypeId: t.trayTypeId,
          excludePayment: t.excludePayment
        }))
        : [
          {
            numberOfTrays: undefined,
            trayTypeId: undefined,
            excludePayment: false
          },
        ],
    });

    setOpen(true);
  };

  const printTransaction = (tx) => {

    const sales = tx.sales || [];

    const trays = tx.trays || [];

    const paymentAmount = Number(tx.paymentAmount) || 0;



    // Logic matches TransactionReceipt.js exactly

    const salesTotal = sales.reduce((sum, sale) => sum + (Number(sale.numberOfItems) * Number(sale.costPerItem) || 0), 0);

    const trayPaymentTotal = trays.reduce((sum, tray) => {
    if (tray.excludePayment) return sum; // Skip calculation for "Return Only" trays

    const trayType = trayTypes.find((t) => t.trayTypeId === tray.trayTypeId || t.id === tray.trayTypeId) || null;
    const cost = trayType?.price || Number(tray.costPerTray) || 0;
    return sum + (Number(tray.numberOfTrays) * cost);
  }, 0);



    const totalCredits = paymentAmount + trayPaymentTotal;

    const transactionBalance = salesTotal - totalCredits;

    const accountBalance = grandTotalTransactionAmount - (grandTotalPaidAmount + grandTrayPaymentAmount);



    const primaryColor = "#1976d2";

    const successColor = "#2e7d32";

    const errorColor = "#d32f2f";



    const printWindow = window.open("", "_blank");

    printWindow.document.write(`

    <!DOCTYPE html>

    <html>

    <head>

      <title>${customerDetails?.name} - ${new Date(tx.dateOfTransaction).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</title>

      <style>

        * { margin: 0; padding: 0; box-sizing: border-box; }

        body { font-family: Arial, sans-serif; padding: 20px; font-size: 14px; }

        .receipt-container { border: 1px solid #ddd; padding: 20px; border-radius: 8px; }



        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }

        th, td { border: 1px solid #333; padding: 8px; }

        th { background: #f5f5f5; font-weight: bold; }



        .section-header {

          background: ${primaryColor} !important;

          color: white !important;

          text-align: center;

          font-size: 15px;

        }



        .text-right { text-align: right; }

        .text-center { text-align: center; }



        @media print {

          body { padding: 10px; }

          .receipt-container { border: none; }

        }

      </style>

    </head>



    <body>

      <div class="receipt-container">



        <!-- HEADER + SUMMARY -->

        <div style="margin-bottom: 25px; border-bottom: 3px solid ${primaryColor}; padding-bottom: 15px;">



          <!-- Branding -->

          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:15px;">

            

            <div style="display:flex; align-items:center; gap:12px;">

              <img src="${LOGO_BASE64}" style="width:60px; height:60px; border-radius:6px;" />

              <div>

                <div style="font-size:22px; font-weight:bold; color:${primaryColor};">

                  ${user?.employerName}

                </div>

                <div style="color:#666; font-size:14px;">

                  ${customerDetails?.branchName || "Transaction Receipt"}

                </div>

              </div>

            </div>



            <div style="text-align:right;">

              <div style="color:#999; font-size:13px;">Transaction Date</div>

              <div style="font-weight:bold; color:${primaryColor};">

                ${new Date(tx.dateOfTransaction).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}

              </div>

            </div>

          </div>



          <!-- Customer -->

          <div style="margin-bottom:15px;">

            <div style="font-size:18px; font-weight:bold; text-transform:capitalize;">

              ${customerDetails?.name || "N/A"}

            </div>

            <div style="color:#666; text-transform:capitalize;">

              ${customerDetails?.location || "N/A"}

            </div>

          </div>



          <!-- TRANSACTION SUMMARY CARD -->

          <div style="

            display:flex;

            justify-content:space-between;

            background:linear-gradient(135deg, ${primaryColor}15, ${primaryColor}08);

            padding:15px;

            border-radius:10px;

            border:1px solid ${primaryColor}30;

          ">
          </div>

        </div>



        <!-- SALES TABLE -->

        ${sales.length > 0 ? `

        <table>

          <thead>

            <tr>

              <th colspan="5" class="section-header" style="padding:10px;">Sales</th>

            </tr>

            <tr>

              <th>#</th>

              <th>Item</th>

              <th class="text-right">Qty</th>

              <th class="text-right">Rate</th>

              <th class="text-right">Amount</th>

            </tr>

          </thead>

          <tbody>

            ${sales.map((s, i) => `

              <tr>

                <td class="text-center">${i + 1}</td>

                <td>${s.itemName}</td>

                <td class="text-right">${s.numberOfItems}</td>

                <td class="text-right">₹${Number(s.costPerItem).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>

                <td class="text-right">₹${(Number(s.numberOfItems) * Number(s.costPerItem)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>

              </tr>

            `).join('')}

          </tbody>

        </table>

        ` : ''}



        <!-- PAYMENT TABLE -->

        ${(paymentAmount > 0 || trays.length > 0) ? `

        <table>

          <thead>

            <tr>

              <th colspan="4" class="section-header" style="background:${successColor} !important;">

                Payment & Tray Returns

              </th>

            </tr>

          </thead>

          <tbody>



            ${paymentAmount > 0 ? `

            <tr style="background:#e8f5e9;">

              <td colspan="3"><strong>Cash / Online</strong></td>

              <td class="text-right" style="color:${successColor}; font-weight:bold;">

                ₹${paymentAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}

              </td>

            </tr>` : ''}



            ${trays.map(t => {

      const type = trayTypes.find(

        (tt) => tt.trayTypeId === t.trayTypeId || tt.id === t.trayTypeId

      ) || null;

      const price = type?.price || 0;

      return `

              <tr style="background:#e8f5e9;">

                <td><strong>Tray Returned</strong></td>

                <td>${type?.label || 'Tray'}</td>

                <td class="text-center">${t.numberOfTrays} × ₹${t.excludePayment ? (0 + '(Payment Excluded)'): price}</td>

                <td class="text-right" style="color:${successColor}; font-weight:bold;">

                  ₹${t.excludePayment ? 0 : (t.numberOfTrays * price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}

                </td>

              </tr>`;

    }).join('')}



          </tbody>

        </table>

        ` : ''}
        <table
              style="
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 15;
              }}
            >
              <tbody>
                <tr style="background: #f5f5f5">
                  <td
                    style="
                      border: 1px solid #333;
                      padding: 10;
                      font-weight: bold;
                    "
                  >
                    Sales Total
                  </td>
                  <td
                    style="
                      border: 1px solid #333;
                      padding: 10;
                      text-align: right;
                      font-weight: bold;
                    "
                  >
                    ₹${formatCurrency(salesTotal)}
                  </td>
                </tr>
                ${totalCredits > 0 ? (
                  `<tr style="background: #e8f5e9">
                    <td
                      style="
                        border: 1px solid #333;
                        padding: 10;
                        font-weight: bold;
                      "
                    >
                      Total Credits
                    </td>
                    <td
                      style="
                        border: 1px solid #333;
                        padding: 10;
                        text-align: right;
                        font-weight: bold;
                        color: successColor;
                      "
                    >
                      - ₹${formatCurrency(totalCredits)}
                    </td>
                  </tr>`
                ) : ``}
                <tr style=" background: #e3f2fd">
                  <td
                    style="
                      border: 1px solid #333;
                      padding: 12;
                      font-weight: bold;
                      font-size: 16;
                    "
                  >
                    ${transactionBalance >= 0 ? "Balance Due" : "Credit Balance"}
                  </td>
                  <td
                    style="
                      border: 1px solid #333;
                      padding: 12;
                      text-align: right;
                      font-weight: bold;
                      fontSize: 18,
                      color:
                        ${transactionBalance >= 0 ? errorColor : successColor},
                    }}
                  >
                    ₹${formatCurrency(Math.abs(transactionBalance))}
                  </td>
                </tr>
              </tbody>
            </table>



        <!-- ACCOUNT BALANCE -->

        <div style="

          text-align:center;

          margin-top:20px;

          padding-top:15px;

          border-top:2px dashed #ccc;

          font-size:16px;

        ">

          <span style="font-weight:bold;">Overall Account Balance:</span>

          <span style="

            font-weight:bold;

            margin-left:10px;

            font-size:18px;

            color:${accountBalance > 0 ? errorColor : successColor};

          ">

            ₹${Math.abs(accountBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}

            ${accountBalance > 0 ? "(Due)" : "(Credit)"}

          </span>

        </div>



      </div>

    </body>

    </html>

`);

    printWindow.document.close();

    printWindow.focus();

    setTimeout(() => {

      printWindow.print();

      printWindow.close();

    }, 250);

  };

  return (
    <>
      {/* Customer Summary */}
      {customerDetails && (
        <Paper
          elevation={2}
          sx={{ p: 2, mb: 0, borderRadius: 0, backgroundColor: "#fafafa" }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Stack
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
              >
                <IconButton onClick={() => navigate(-1)} sx={{ p: 0 }}>
                  <ArrowBack />
                </IconButton>
                <Typography variant="h5" fontWeight="bold">
                  {customerDetails?.name || "—"}
                </Typography>
              </Stack>
              <Stack
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={3}
              >
                <Typography
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <MdPhoneAndroid size={18} />
                  {customerDetails?.mobile || "N/A"}
                </Typography>
                <Typography
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <MdLocationOn size={18} />
                  {customerDetails?.location || "N/A"}
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => setOpenTrayModal(true)}
                  size="small"
                >
                  Tray Balance
                </Button>

                <Button
                  variant="contained"
                  onClick={() => navigate("/tray-history/" + params?.id)}
                  size="small"
                >
                  Tray History
                </Button>
              </Stack>
            </Box>
            <Box textAlign="right" display={"flex"} gap={4}>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Transaction Value
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ₹ {grandTotalTransactionAmount.toLocaleString("en-IN")}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Amount Paid
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ₹{" "}
                  {(grandTotalPaidAmount + grandTrayPaymentAmount).toLocaleString(
                    "en-IN"
                  )}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="subtitle2" color="text.secondary">
                  Total Amount Due
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  ₹{" "}
                  {(
                    grandTotalTransactionAmount -
                    (grandTotalPaidAmount + grandTrayPaymentAmount)
                  ).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
      <Box sx={{ p: 2, py: 0 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          my={2.5}
        >
          <Typography variant="h6" fontWeight="bold">
            Transactions
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" alignItems="center" gap={1}>
                <DatePicker
                  label="From"
                  value={dateRange.startDate}
                  maxDate={dateRange.endDate}
                  onChange={(v) => {
                    if (!v || !v.isValid()) return;
                    setDateRange((p) => ({ ...p, startDate: v }));
                  }}
                  slotProps={{
                    textField: { size: "small", sx: { width: 160 } },
                  }}
                />

                <DatePicker
                  label="To"
                  value={dateRange.endDate}
                  minDate={dateRange.startDate}
                  maxDate={dayjs()}
                  onChange={(v) => {
                    if (!v || !v.isValid()) return;
                    setDateRange((p) => ({ ...p, endDate: v }));
                  }}
                  slotProps={{
                    textField: { size: "small", sx: { width: 160 } },
                  }}
                />

                {/*CLEAR BUTTON */}
                <Button
                  size="small"
                  variant="text"
                  onClick={clearDateFilter}
                  disabled={!dateRange.startDate && !dateRange.endDate}
                >
                  Reset
                </Button>
              </Box>
            </LocalizationProvider>

            <Button
              variant="outlined"
              onClick={printPDF}
              disabled={paginatedTransactions.length == 0}
            >
              Print PDF
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Transaction
            </Button>
          </Box>
        </Box>

        {/* Table */}

        {loadingTx ? (
          <Box display="flex" flexDirection="column" alignItems="center" my={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading transactions...
            </Typography>
          </Box>
        ) : transactionList.length === 0 ? (
          <Box display="flex" justifyContent="center" my={2}>
            <Typography
              color="text.secondary"
              variant="h5"
              fontWeight="bold"
              sx={{
                background: "#fafafa",
                py: "40px",
                display: "block",
                width: "100%",
                textAlign: "center",
              }}
            >
              No transactions found.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 0,
                border: "1px solid #e0e0e0",
                maxHeight: "calc(100vh - 227px)",
                overflow: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  {/* Group Header Row */}
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        borderRight: "2px solid #e0e0e0",
                        py: 1,
                        backgroundColor: "#ffffff",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Sales</strong>
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Trays Returned</strong>
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        py: 0.2,
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Payment</strong>
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        py: 0.2,
                        backgroundColor: "#ffffff",
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Transaction By</strong>
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        py: 0.2,
                        backgroundColor: "#ffffff",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                  {/* Column Header Row */}
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Item</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Qty</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Price</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Qty</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Price</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#e4ffe4",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        py: 0.2,
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Payment Received</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        py: 0.2,
                        borderRight: "2px solid #e0e0e0",
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Tray Payment</strong>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "2px solid #e0e0e0",
                        py: 0.2,
                        position: "sticky",
                        top: "28px",
                        zIndex: 2,
                      }}
                    >
                      <strong>Total</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((tx) => {
                    const sales = tx.sales || [];
                    const trays = tx.trays || [];

                    const rowCount = Math.max(sales.length, trays.length, 1);
                    const paymentAmount = tx.paymentAmount
                      ? tx.paymentAmount
                      : 0;

                    const traysTotal = (tx.trays || []).reduce((sum, t) => {
                      const qty = toNumber(t?.numberOfTrays);
                      const cost = toNumber(t?.costPerTray);
                      return sum + qty * cost;
                    }, 0);
                    const grandTotal = paymentAmount + traysTotal;

                    return (
                      <>
                        {Array.from({ length: rowCount }).map((_, index) => {
                          const tray = tx.trays?.[index];
                          const sale = sales[index];

                          const trayTotal =
                            tray?.numberOfTrays != null &&
                              tray?.costPerTray != null
                              ? tray.numberOfTrays * tray.costPerTray
                              : "-";

                          return (
                            <TableRow key={index}>
                              {index === 0 && (
                                <TableCell
                                  rowSpan={rowCount}
                                  sx={{ borderRight: "2px solid #e0e0e0" }}
                                >
                                  {new Date(
                                    tx.dateOfTransaction
                                  ).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    timeZone: "Asia/Kolkata",
                                  })}
                                </TableCell>
                              )}

                              {/* SALES */}
                              <TableCell
                                sx={{ borderRight: "2px solid #e0e0e0" }}
                              >
                                {val(sale?.itemName)}
                              </TableCell>
                              <TableCell
                                sx={{ borderRight: "2px solid #e0e0e0" }}
                              >
                                {val(sale?.numberOfItems)}
                              </TableCell>
                              <TableCell
                                sx={{ borderRight: "2px solid #e0e0e0" }}
                              >
                                {val(sale?.costPerItem)}
                              </TableCell>
                              <TableCell
                                sx={{ borderRight: "2px solid #e0e0e0" }}
                              >
                                {val(sale?.total)}
                              </TableCell>

                              {/* TRAYS */}
                              <TableCell
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                  background: "#e4ffe4",
                                }}
                              >
                                {val(tray?.numberOfTrays)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                  background: "#e4ffe4",
                                }}
                              >
                                {val(tray?.costPerTray)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderRight: "2px solid #e0e0e0",
                                  background: "#e4ffe4",
                                }}
                              >
                                {trayTotal}
                              </TableCell>

                              {/* TOTALS */}
                              {index === 0 && (
                                <>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{ borderRight: "2px solid #e0e0e0" }}
                                  >
                                    {val(paymentAmount) || "-"}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{
                                      borderRight: "2px solid #e0e0e0",
                                    }}
                                  >
                                    {val(traysTotal) ? val(traysTotal) : "-"}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{ borderRight: "2px solid #e0e0e0" }}
                                  >
                                    {val(grandTotal) ? val(grandTotal) : "-"}
                                  </TableCell>
                                  <TableCell
                                    rowSpan={rowCount}
                                    sx={{ borderRight: "2px solid #e0e0e0" }}
                                  >
                                    {val(tx.transactionBy?.fullName)} <br /> {new Date(tx?.createdAt).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      timeZone: "Asia/Kolkata",
                                    })}
                                  </TableCell>
                                  <TableCell rowSpan={rowCount}>
                                    <Box display="flex" alignItems="center">
                                      {user?.roleId !== 3 && (
                                        <IconButton
                                          color="primary"
                                          onClick={() =>
                                            openEditTransactionPopup(tx)
                                          }
                                        >
                                          <FaEdit size={19} />
                                        </IconButton>
                                      )}
                                      <IconButton
                                        color="primary"
                                        onClick={() =>
                                          printTransaction(tx)
                                        }
                                      >
                                        <FaPrint size={19} />
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={transactionList.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}

        {/* Transaction Dialog */}
        <Dialog open={open} maxWidth="md" fullWidth>
          <DialogTitle
            variant="h6"
            fontWeight="bold"
            sx={{
              background: "#f5f5f5",
            }}
          >
            {editTransaction ? "Edit Transaction" : "Add Transaction"}
          </DialogTitle>

          <form onSubmit={handleSubmit(addTransaction)}>
            <DialogContent>
              <Box display="flex" gap={4}>
                <FormControlLabel
                  control={
                    <Controller
                      name="stockGiven"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Sales"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="paymentDone"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Payment Received"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="traysReturned"
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                    />
                  }
                  label="Tray Payment"
                />
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="dateOfTransaction"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      sx={{ mt: 3 }}
                      label="Date of Transaction"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(v) => field.onChange(v ? v.toDate() : null)}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: false,
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              {stockGiven && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="p" fontWeight="bold">
                    Sales
                  </Typography>

                  {salesFields.map((row, index) => {
                    const qty = watch(`sales.${index}.numberOfItems`);
                    const cost = watch(`sales.${index}.costPerItem`);

                    return (
                      <Box
                        key={row.id}
                        display="flex"
                        gap={2}
                        mt={2}
                        alignItems={"center"}
                      >
                        {/* <TextField
                          label="Item Name"
                          sx={{ width: "45%", flex: 1 }}
                          {...register(`sales.${index}.itemName`)}
                          error={!!errors?.sales?.[index]?.itemName}
                          helperText={errors?.sales?.[index]?.itemName?.message}
                        /> */}
                        <FormControl
                          sx={{ flex: 1 }}
                          error={!!errors?.sales?.[index]?.itemName}
                        >
                          <InputLabel>Item</InputLabel>

                          <Controller
                            name={`sales.${index}.itemName`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Item"
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(e.target.value)
                                }
                              >
                                {items.map((item) => (
                                  <MenuItem key={item._id} value={item.itemName}>
                                    {item.itemName}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />

                          <FormHelperText>
                            {errors?.sales?.[index]?.itemName?.message}
                          </FormHelperText>
                        </FormControl>
                        <FormControl
                          sx={{ flex: 1 }}
                          error={!!errors?.sales?.[index]?.trayTypeId}
                        >
                          <InputLabel>Tray Type</InputLabel>

                          <Controller
                            name={`sales.${index}.trayTypeId`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Tray Type"
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                              >
                                {activeTrayTypes.map((tray) => (
                                  <MenuItem
                                    key={tray.trayTypeId || tray.id}
                                    value={tray.trayTypeId || tray.id}
                                  >
                                    {tray.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />

                          <FormHelperText>
                            {errors?.sales?.[index]?.trayTypeId?.message}
                          </FormHelperText>
                        </FormControl>

                        <TextField
                          sx={{ flex: 1 }}
                          label="Qty"
                          type="number"
                          {...register(`sales.${index}.numberOfItems`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.sales?.[index]?.numberOfItems}
                          helperText={
                            errors?.sales?.[index]?.numberOfItems?.message
                          }
                        />
                        <TextField
                          label="Cost"
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          sx={{ flex: 1 }}
                          {...register(`sales.${index}.costPerItem`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.sales?.[index]?.costPerItem}
                          helperText={
                            errors?.sales?.[index]?.costPerItem?.message
                          }
                        />
                        <TextField
                          label="Amount"
                          readonly
                          sx={{ flex: 1 }}
                          value={qty && cost ? qty * cost : ""}
                        />

                        {salesFields.length > 1 && (
                          <Button color="error" onClick={() => remove(index)}>
                            <FaTrash />
                          </Button>
                        )}
                      </Box>
                    );
                  })}

                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    onClick={() =>
                      append({
                        itemName: "",
                        numberOfItems: "",
                        costPerItem: "",
                      })
                    }
                  >
                    + Add More
                  </Button>
                </>
              )}

              {paymentDone && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="p" fontWeight="bold">
                    Payment
                  </Typography>

                  <Box display="flex" gap={2} mt={2}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      {...register(`paymentAmount`, {
                        setValueAs: (v) => (v === "" ? undefined : Number(v)),
                      })}
                      error={!!errors?.paymentAmount}
                      helperText={errors?.paymentAmount?.message}
                    />
                  </Box>
                </>
              )}

              {traysReturned && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography fontWeight="bold">
                      Tray Payment
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={excludePayment}
                          onChange={(e) => setExcludePayment(e.target.checked)}
                        />
                      }
                      label="Exclude Payment"
                    />
                  </Box>

                  {trayForm.fields.map((row, index) => {
                    const qty = watch(`trays.${index}.numberOfTrays`) ?? 0;
                    const trayId = watch(`trays.${index}.trayTypeId`);
                    const isExcluded = excludePayment;
                    const selectedTrayType =
                      trayId && trayTypes.length
                        ? trayTypes.find(
                          (tray) =>
                            tray.trayTypeId === trayId || tray.id === trayId
                        )
                        : null;
                    const costOfSelectedTray = selectedTrayType?.price || 0;
                    const availableBalance = getTrayBalanceCount(trayId);

                    const originalQty =
                      editTransaction &&
                        selectedTransaction?.trays?.[index]?.numberOfTrays
                        ? Number(selectedTransaction.trays[index].numberOfTrays)
                        : 0;

                    const maxQty = editTransaction
                      ? availableBalance + originalQty
                      : availableBalance;

                    const availableTrayTypes = (customerDetails?.trayBalance || []).filter((tray) => {
                      const currentTrayId = tray.trayTypeId || tray.id;

                      if (currentTrayId === trayId) return true;
                      const hasValidCount = tray.count !== undefined && tray.count !== null && Number(tray.count) > 0;

                      const isNotSelected = !selectedTrayTypeIds.includes(currentTrayId);

                      return hasValidCount && isNotSelected;
                    });

                    return (
                      <div>
                        <Box
                          key={row.id}
                          display="flex"
                          gap={2}
                          mt={2}
                          alignItems="center"
                        >

                          <FormControl
                            sx={{ flex: 1 }}
                            error={!!errors?.trays?.[index]?.trayTypeId}
                          >
                            <InputLabel>Tray Type</InputLabel>

                            <Controller
                              name={`trays.${index}.trayTypeId`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  label="Tray Type"
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                >
                                  {availableTrayTypes.map((tray) => (
                                    <MenuItem key={tray.id} value={tray.id}>
                                      {tray.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />

                            <FormHelperText>
                              {errors?.trays?.[index]?.trayTypeId?.message}
                            </FormHelperText>
                          </FormControl>

                          <TextField
                            sx={{ flex: 1 }}
                            label="Qty"
                            type="number"
                            inputProps={{
                              min: 1,
                              max: maxQty,
                            }}
                            {...register(`trays.${index}.numberOfTrays`, {
                              setValueAs: (v) =>
                                v === "" ? undefined : Number(v),
                              validate: (value) =>
                                value <= maxQty || `Maximum allowed is ${maxQty}`,
                            })}
                            error={!!errors?.trays?.[index]?.numberOfTrays}
                            helperText={
                              errors?.trays?.[index]?.numberOfTrays?.message
                            }
                          />
                          {/* <TextField
                          label="Cost"
                          type="number"
                          {...register(`trays.${index}.costPerTray`, {
                            setValueAs: (v) =>
                              v === "" ? undefined : Number(v),
                          })}
                          error={!!errors?.trays?.[index]?.costPerTray}
                          helperText={
                            errors?.trays?.[index]?.costPerTray?.message
                          }
                        /> */}

                          {!isExcluded && (
                            <TextField
                              label="Amount"
                              slotProps={{ input: { readOnly: true } }}
                              value={qty * costOfSelectedTray}
                              sx={{ flex: 1 }}
                            />
                          )}

                          {trayForm.fields.length > 1 && (
                            <Button
                              color="error"
                              onClick={() => trayForm.remove(index)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </Box>
                      </div>
                    );
                  })}

                  {selectedTrayTypeIds.length < filteredTrayTypes.length && (
                    <Button
                      sx={{ mt: 2 }}
                      variant="outlined"
                      onClick={() =>
                        trayForm.append({
                          numberOfTrays: "",
                          costPerTray: "",
                        })
                      }
                    >
                      + Add More
                    </Button>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  closeTransactionPopup();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? "Saving" : "Submit"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ✅ Payment Dialog */}
        <Dialog
          open={openPayment}
          fullWidth
          maxWidth="sm"
          onClose={closePaymentPopup}
        >
          <DialogTitle>Add Payment</DialogTitle>

          <form noValidate onSubmit={paymentForm.handleSubmit(updatePayment)}>
            <DialogContent>
              <Box display="grid" gap={2} mt={1}>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Amount"
                    type="number"
                    fullWidth
                    required
                    {...paymentForm.register("amount")}
                    error={!!paymentForm.formState.errors.amount}
                    helperText={paymentForm.formState.errors.amount?.message}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 6 }}>₹</span>,
                    }}
                  />

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="paymentDate"
                      control={paymentForm.control}
                      render={({ field, fieldState }) => (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Payment Date"
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(newValue) =>
                              field.onChange(
                                newValue ? newValue.toDate() : null
                              )
                            }
                            maxDate={dayjs()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                error: !!fieldState?.error,
                                helperText: fieldState?.error?.message,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </LocalizationProvider>
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    select
                    label="Payment Method"
                    fullWidth
                    required
                    defaultValue=""
                    {...paymentForm.register("paymentMethod")}
                    error={!!paymentForm.formState.errors.paymentMethod}
                    helperText={
                      paymentForm.formState.errors.paymentMethod?.message
                    }
                  >
                    <MenuItem value="NEFT">NEFT</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="Net Banking">Net Banking</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                  </TextField>

                  <TextField
                    label="Reference ID"
                    fullWidth
                    {...paymentForm.register("referenceId")}
                    error={!!paymentForm.formState.errors.referenceId}
                    helperText={
                      paymentForm.formState.errors.referenceId?.message
                    }
                    placeholder="Enter transaction or reference number"
                  />
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closePaymentPopup}>Cancel</Button>
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <TrayBalanceModal
          open={openTrayModal}
          onClose={() => setOpenTrayModal(false)}
          trayBalance={customerDetails?.trayBalance}
        />
        <TransactionReceipt
          open={showReceipt}
          onClose={() => {
            setShowReceipt(false);
            setReceiptTransaction(null);
          }}
          transaction={receiptTransaction}
          customerDetails={customerDetails}
          accountBalanceData={{
            totalTransactionAmount: grandTotalTransactionAmount,
            totalPaidAmount: grandTotalPaidAmount,
            trayPaymentAmount: grandTrayPaymentAmount,
            totalAmountDue:
              grandTotalTransactionAmount - (grandTotalPaidAmount + grandTrayPaymentAmount),
          }}
          trayTypes={trayTypes}
          employerName={user?.employerName}
        />
      </Box>
    </>
  );
}

export default CustomerDetails;
